import { useState } from "react";
import { Scale } from "lucide-react";
import { useApi, type AiProposal } from "@ncp/api-client";
import { DraftSources, DraftTextEnd } from "@ncp/assistant-sdk";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, InfoHint, MarkdownBody, SourceText, Textarea, cn, hasArabicBody, useSave } from "@ncp/ui";

const OUTCOMES = ["Infringement", "NoInfringement"] as const;
const MIN_REASON = 40;
/** The shortest write-up of an outcome the server accepts, and the placeholder the draft leaves on an outcome it did not write up (Condition 3.6). */
const MIN_WRITE_UP = 20;
const NOT_WRITTEN_UP = "Not written up by the draft; the officers must write it up before choosing.";

interface ReportOutcome { code: string; chosen?: boolean; reasoning?: string; evidenceFor?: string[]; evidenceAgainst?: string[] }
const str = (v: unknown) => (typeof v === "string" ? v : "");

/**
 * The final report of a complaint or whistleblowing investigation writes up both lawful outcomes; the officers choose one and state their
 * own reasons when they approve it, and the server refuses the approval without them. A pick the draft made is shown beside the choice,
 * never preselected, and kept on the approved report so departures from it can be counted.
 */
export function FinalReportOutcomeCard({ report, recordable, isNext, onSaved }: { report: AiProposal; recordable: boolean; isNext: boolean; onSaved: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const structured = (report.structured ?? {}) as Record<string, unknown>;
  const written = (Array.isArray(structured.outcomes) ? structured.outcomes : []) as ReportOutcome[];
  const pending = report.status === "Proposed";
  // Pending, `recommendation` is the draft's own pick; once approved it is the officers' choice and the draft's pick moves to `draftRecommendation`.
  const draftPick = pending ? str(structured.recommendation) : str(structured.draftRecommendation);
  const officers = pending ? "" : str(structured.recommendation);
  const [choice, setChoice] = useState("");
  const [reason, setReason] = useState("");
  // Both lawful outcomes are written up before approval: the officers write up the one the draft left out (Condition 3.6).
  const [writeUps, setWriteUps] = useState<Record<string, string>>({});
  const unwritten = (o?: ReportOutcome) => !o?.reasoning || o.reasoning.trim() === NOT_WRITTEN_UP || o.reasoning.trim().length < MIN_WRITE_UP;
  const missing = OUTCOMES.filter((code) => unwritten(written.find((o) => o.code === code)) && (writeUps[code] ?? "").trim().length < MIN_WRITE_UP);
  const label = (code: string) => t(`decisionOutcomes.${code}`, { defaultValue: code });
  // Condition 3.10: the report counts as opened once the end of its full text below has been on screen, and its sources once one is opened –
  // as on the shared review card, never on loading the card.

  const approve = useSave({
    mutationFn: () => api.post<{ succeeded: boolean; error?: string }>(`/api/ai/proposals/${report.id}/approve`, {
      note: null,
      editedStructuredJson: JSON.stringify({
        ...structured,
        recommendation: choice,
        draftRecommendation: draftPick,
        officerReason: reason.trim(),
        outcomes: OUTCOMES.map((code) => {
          const o = written.find((x) => x.code === code) ?? { code, reasoning: "", evidenceFor: [], evidenceAgainst: [] };
          const own = (writeUps[code] ?? "").trim();
          return { ...o, ...(unwritten(o) && own ? { reasoning: own, writtenByOfficers: true } : {}), chosen: code === choice };
        }),
      }),
    }),
    saved: t("backoffice:investigation.outcome.approved"),
    onSuccess: () => onSaved(),
  });

  const selected = pending ? choice : officers;
  const canApprove = pending && recordable;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Scale className="size-4 text-aegold-700" /> {t("backoffice:investigation.outcome.title")}
          {officers ? <Badge size="sm" variant={officers === "Infringement" ? "danger" : "success"}>{label(officers)}</Badge> : null}
          {officers && draftPick && draftPick !== officers ? <Badge size="sm" variant="warning">{t("backoffice:investigation.outcome.departs")}</Badge> : null}
          <InfoHint>{t("backoffice:investigation.outcome.hint")}</InfoHint>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-muted-foreground">{draftPick ? t("backoffice:investigation.outcome.draftPick", { outcome: label(draftPick) }) : t("backoffice:investigation.outcome.draftNone")}</p>
        <div className="grid gap-3 md:grid-cols-2" role={canApprove ? "radiogroup" : undefined} aria-label={t("backoffice:investigation.outcome.choose")}>
          {OUTCOMES.map((code) => {
            const o = written.find((x) => x.code === code);
            const active = selected === code;
            return (
              <button key={code} type="button" role={canApprove ? "radio" : undefined} aria-checked={canApprove ? active : undefined} disabled={!canApprove}
                onClick={() => setChoice(code)}
                className={cn("rounded-xl border p-3 text-start text-xs transition-colors", active ? "border-aegold-500 bg-aegold-50/60 ring-1 ring-aegold-400" : "border-sand-200", canApprove ? "hover:border-aegold-300" : "cursor-default")}>
                <p className="text-sm font-semibold">{label(code)}</p>
                {o?.reasoning && !unwritten(o) ? <p className="mt-1 whitespace-pre-wrap"><SourceText text={o.reasoning} /></p> : <p className="mt-1 text-muted-foreground">{t("backoffice:investigation.outcome.notWrittenUp")}</p>}
                {(["evidenceFor", "evidenceAgainst"] as const).map((side) => (o?.[side]?.length ? (
                  <p key={side} className="mt-1.5 flex flex-wrap items-center gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t(`backoffice:investigation.outcome.${side}`)}</span>
                    {o[side]!.map((ref) => <Badge key={ref} size="sm" variant={side === "evidenceFor" ? "success" : "danger"}>{ref}</Badge>)}
                  </p>
                ) : null))}
              </button>
            );
          })}
        </div>
        {canApprove ? (
          <section className="rounded-xl border border-sand-200" aria-label={t("backoffice:investigation.outcome.fullText")}>
            <p className="border-b border-sand-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:investigation.outcome.fullText")}</p>
            <div className="max-h-96 overflow-y-auto px-3 py-2 scrollbar-thin" tabIndex={0}>
              <MarkdownBody className="prose-sm">{locale === "ar" && hasArabicBody(report) ? report.contentMarkdownAr! : report.contentMarkdown}</MarkdownBody>
              <DraftTextEnd proposal={report} enabled={recordable} />
            </div>
            <p className="border-t border-sand-100 px-3 py-1.5 text-[11px] text-muted-foreground">{t("backoffice:investigation.outcome.fullTextHint")}</p>
          </section>
        ) : null}
        {canApprove ? <DraftSources proposal={report} /> : null}
        {canApprove ? (
          <div className="grid gap-2">
            {OUTCOMES.filter((code) => unwritten(written.find((o) => o.code === code))).map((code) => (
              <Field key={code} label={t("backoffice:investigation.outcome.writeUp", { outcome: label(code) })} hint={t("backoffice:investigation.outcome.writeUpHint", { min: MIN_WRITE_UP })} required>
                {(id) => <Textarea id={id} rows={3} value={writeUps[code] ?? ""} onChange={(e) => setWriteUps((w) => ({ ...w, [code]: e.target.value }))} draft={`proposal:${report.id}:outcome:${code}`} />}
              </Field>
            ))}
            <Field label={t("backoffice:investigation.outcome.reason")} hint={t("backoffice:investigation.outcome.reasonHint", { min: MIN_REASON })} required>
              {(id) => <Textarea id={id} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} draft={`proposal:${report.id}:outcome:reason`} />}
            </Field>
            <div className="flex justify-end">
              <Button variant="gold" size="sm" primary={isNext} disabled={!choice || reason.trim().length < MIN_REASON || missing.length > 0} loading={approve.isPending} onClick={() => approve.mutate()}>{t("backoffice:investigation.outcome.approve")}</Button>
            </div>
          </div>
        ) : officers ? (
          <div className="rounded-xl bg-sand-50 p-3 text-xs">
            <p className="text-muted-foreground">{t("backoffice:investigation.outcome.chosen", { date: report.reviewedAt ? formatDate(report.reviewedAt, locale) : "—", outcome: label(officers) })}</p>
            {str(structured.officerReason) ? <p className="mt-1 whitespace-pre-wrap"><span className="font-semibold">{t("backoffice:investigation.outcome.reasonLabel")}: </span><SourceText text={str(structured.officerReason)} /></p> : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
